import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from './../components/Message';
import Loader from './../components/Loader';
import { useGetDailyStocksQuery, useDeleteDailyStockMutation } from './../slices/dailyStocksApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';

const StaffDailyStock = () => {
  // State variables for filtering, pagination, and date range
  const [filter, setFilter] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch paginated dailyStocks data
  const { data, isLoading, error, refetch } = useGetDailyStocksQuery(pageNumber);

  // Mutation for deleting dailyStocks
  const [deleteDailyStock, { isLoading: loadingDelete }] = useDeleteDailyStockMutation();

  // Handle dailyStock deletion
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this dailyStock?')) {
      try {
        await deleteDailyStock(id).unwrap();
        toast.success('DailyStock deleted successfully!');
        refetch(); // Refetch dailyStocks after deletion
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete dailyStock.');
      }
    }
  };

  // Filter dailyStocks based on date range and search criteria
  const filterDailyStocks = () => {
    if (!data?.dailyStocks) return [];

    // 1) Filter by date range
    let filteredDailyStocks = data.dailyStocks.filter((dailyStock) => {
      const dailyStockDate = new Date(dailyStock.date);
      return (
        (!startDate || dailyStockDate >= new Date(startDate)) &&
        (!endDate || dailyStockDate <= new Date(endDate))
      );
    });

    // 2) Apply general search filter against dailyStock note and product names within dailyStockItems.
    if (filter.trim()) {
      const lowerFilter = filter.toLowerCase();
      filteredDailyStocks = filteredDailyStocks.filter((dailyStock) => {
        const noteMatch = dailyStock.note?.toLowerCase().includes(lowerFilter);
        const itemMatch = dailyStock.dailyStockItems.some((item) =>
          item.name.toLowerCase().includes(lowerFilter)
        );
        return noteMatch || itemMatch;
      });
    }

    // 3) Further filter dailyStock items by product name ONLY using productSearch.
    if (productSearch.trim()) {
      const lowerProduct = productSearch.toLowerCase();
      filteredDailyStocks = filteredDailyStocks
        .map((dailyStock) => {
          return {
            ...dailyStock,
            dailyStockItems: dailyStock.dailyStockItems.filter((item) =>
              item.name.toLowerCase().includes(lowerProduct)
            ),
          };
        })
        .filter((dailyStock) => dailyStock.dailyStockItems.length > 0);
    }

    return filteredDailyStocks;
  };

  // Calculate total dailyStocks value based on filtered dailyStocks
  const calculateTotalDailyStocks = () => {
    return filterDailyStocks().reduce((total, dailyStock) => {
      const dailyStockTotal = dailyStock.dailyStockItems.reduce(
        (subTotal, item) => subTotal + item.qty * item.price,
        0
      );
      return total + dailyStockTotal;
    }, 0);
  };

  // Handle pagination changes
  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>DailyStocks</h1>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            placeholder="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            placeholder="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>Total DailyStocks: {calculateTotalDailyStocks().toFixed(2)}</h4>
        </Col>
      </Row>

      {/* Additional product-only search box */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by product name only"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'An error occurred'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterDailyStocks().map((dailyStock) =>
                dailyStock.dailyStockItems.map((item) => (
                  <tr key={`${dailyStock._id}-${item.product}`}>
                    <td>{new Date(dailyStock.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>{(item.qty * item.price).toFixed(2)}</td>
                    <td>{dailyStock.note || 'N/A'}</td>
                    <td>
                      {item.images && (
                        <img
                          src={item.images[0]}
                          alt="Thumbnail"
                          className="thumbnail-img me-2"
                          style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/dailyStock/${dailyStock._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                          as={Link}
                          to={`/copy/dailyStock/${dailyStock._id}/copy`}
                          variant="light"
                          className="btn-sm mx-2"
                     >
                       Copy
                      </Button>
                      <Button
                        style={{ display: 'none' }}
                        as={Link}
                        to={`/dailyStock/${dailyStock._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                        style={{ display: 'none' }}
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(dailyStock._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default StaffDailyStock;